"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { Sheet, SheetContent, SheetTitle } from "./sheet";

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  headerAction?: React.ReactNode;
  showDragHandle?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  desktopPlacement?: "center" | "bottom";
}

export function BottomSheet({
  isOpen,
  onClose,
  children,
  title,
  headerAction,
  showDragHandle = true,
  className,
  overlayClassName,
  contentClassName,
  desktopPlacement = "center",
}: BottomSheetProps) {
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const fallbackTitle = "Bottom sheet dialog";

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          "fixed! bottom-0! border-none bg-transparent p-0 shadow-none focus:outline-none md:fixed! md:left-1/2 md:right-auto! md:w-full md:max-w-md md:-translate-x-1/2",
          desktopPlacement === "bottom"
            ? "md:bottom-0! md:top-auto! md:translate-y-0"
            : "md:bottom-auto! md:top-1/2 md:-translate-y-1/2",
          contentClassName,
        )}
        overlayClassName={overlayClassName}
      >
        <div
          className={cn(
            "mx-auto w-full max-w-md rounded-t-2xl max-h-[70vh] md:max-h-[90dvh] overflow-y-auto overflow-x-hidden bg-white px-4 pb-6 pt-3 md:shadow-2xl",
            desktopPlacement === "center" && "md:rounded-2xl",
            className,
          )}
        >
          {showDragHandle && (
            <div
              className={cn(
                "mb-4 flex justify-center",
                desktopPlacement === "center" && "md:hidden",
              )}
            >
              <div className="h-[3px] w-12 rounded-full bg-[#B7B5AF]" />
            </div>
          )}

          {title || headerAction ? (
            <div className="mb-4 flex items-center justify-between gap-3">
              <SheetTitle
                className={cn(
                  "text-lg font-semibold text-[#344054]",
                  !title && "sr-only",
                )}
              >
                {title ?? fallbackTitle}
              </SheetTitle>
              {headerAction}
            </div>
          ) : (
            <SheetTitle className="sr-only">{fallbackTitle}</SheetTitle>
          )}

          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
